// src/pages/Instrument_Runs/components/RunEditForm/RunEditForm.tsx
import { instrumentRunQueryKeys } from '@/config/queryKeys';
import { useApiClient } from '@/hooks/useApiClient';
import { extractFieldErrors } from '@/lib/api-errors';
import { applyServerFieldErrors, clearServerFieldErrors } from '@/lib/form-errors';
import { applyInstrumentRunsListRefresh } from '@/lib/instrumentRunsRefresh';
import { type Triple, mapFieldErrorsToForm } from '@/lib/map-field-errors';
import SampleManager from '@/pages/Instrument_Runs/components/SampleManager/SampleManager';
import { useAppStore } from '@/stores/app';
import type {
  InstrumentRunAssayVersion,
  InstrumentRunConsumables as InstrumentRunConsumablesType,
  InstrumentRunDataRow,
  InstrumentRunDraftCreate,
  InstrumentRunDraftUpdate,
  InstrumentRunForm,
  InstrumentRunFormValues,
  InstrumentRunPublic,
  InstrumentRunSampleSize,
  InstrumentRunSpecies,
  Org,
  StudyPublicGetMany,
} from '@/types';
import { INSTRUMENT_RUN_ASSAY_VERSIONS } from '@/types';
import { CheckboxGroup } from '@nautilusbio/nb-design-system';
import { Button } from '@nautilusbio/nb-design-system/Button';
import { Grid } from '@nautilusbio/nb-design-system/Grid';
import { Input } from '@nautilusbio/nb-design-system/Input';
import { Select } from '@nautilusbio/nb-design-system/Select';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Textarea } from '@nautilusbio/nb-design-system/Textarea';
import { toast } from '@nautilusbio/nb-design-system/Toaster';
import { useForm, useStore } from '@tanstack/react-form';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { CloudUploadIcon, SaveIcon, XIcon } from 'lucide-react';
import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import z from 'zod';
import { useShallow } from 'zustand/react/shallow';

import irStyles from '../../styles.module.css';
import InstrumentRunConsumables from '../InstrumentRunConsumables';

import ControlsSection from './ControlsSection';
import styles from './styles.module.css';

/**
 * Counts sample rows whose `name` duplicates another row's name within the same run.
 * Returns the total number of offending rows (e.g. two rows sharing a name → 2), used to
 * report in-table duplicates distinctly from backend "already exists" conflicts.
 */
function countDuplicateSampleNames(samples: InstrumentRunFormValues['samples']): number {
  const counts = new Map<string, number>();
  for (const s of samples) {
    const key = s.name?.trim();
    if (key) counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  let total = 0;
  for (const n of counts.values()) {
    if (n > 1) total += n;
  }
  return total;
}

const EDIT_READONLY_COLUMNS = ['flow_cell', 'lane', 'well_position', 'demux_index', 'org_name'];
const ALLOWED_SPECIES = ['Human', 'Mouse'] as const;
const NO_OF_SAMPLES_OPTIONS = ['4', '12', '24', '36'] as const;
const BUTTON_ICON_SIZE = 18;

export type RunEditFormHandle = {
  submit: () => void;
  saveDraft: () => void;
  submitDraft: () => void;
  form: InstrumentRunForm;
};

export type RunEditFormProps = {
  mode: 'create' | 'edit';
  run?: InstrumentRunPublic;
  orgId: string;
  organizations: Org[];
  studies: StudyPublicGetMany[];
  showHeader?: boolean;
  onSave: () => void;
  onCancel?: () => void;
};

const RunEditForm = forwardRef<RunEditFormHandle, RunEditFormProps>(function RunEditForm(
  { mode, run, orgId, organizations, showHeader, onSave, onCancel },
  ref,
) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const api = useApiClient();
  const queryClient = useQueryClient();

  const setShouldRefreshInstrumentRuns = useAppStore(useShallow(state => state.setShouldRefreshInstrumentRuns));

  const orgNameToId = useMemo(() => {
    const map = new Map<string, string>();
    for (const org of organizations) map.set(org.name, String(org.id));
    return map;
  }, [organizations]);

  const orgIdToName = useMemo(() => {
    const map = new Map<string, string>();
    for (const org of organizations) map.set(String(org.id), org.name);
    return map;
  }, [organizations]);

  const initialValues = useMemo((): InstrumentRunFormValues => {
    if (mode === 'edit' && run) {
      const samples = (run.samples ?? [])
        .filter(s => !s.is_control)
        .map(s => ({
          name: s.name ?? '',
          study_name: s.study_name ?? '',
          org_name: s.org_name ?? (s.org_id ? (orgIdToName.get(String(s.org_id)) ?? '') : ''),
          org_id: s.org_id ?? '',
          well_position: s.well_position ?? '',
          demux_index: s.demux_index ?? 0,
          is_control: s.is_control ?? false,
          control_type: s.control_type ?? '',
          flow_cell: (s as InstrumentRunDataRow).flow_cell ?? null,
          lane: (s as InstrumentRunDataRow).lane ?? null,
          library_group: (s as InstrumentRunDataRow).library_group ?? null,
          sample_group: (s as InstrumentRunDataRow).sample_group ?? null,
        }));

      return {
        name: run.name ?? '',
        description: run.description ?? '',
        species: (run.species ?? 'Human') as InstrumentRunSpecies,
        noOfSamples: String(run.num_samples ?? 12),
        assay_version: (run.assay_version ?? INSTRUMENT_RUN_ASSAY_VERSIONS[0]) as InstrumentRunAssayVersion,
        samples,
        // Safe to default false even for 24-sample edits: getControlRows ignores this flag
        // for 24-sample runs (A4 is always required there) and the checkbox is hidden. Once
        // controls persist (backend follow-up), derive this from the saved A4 control row.
        useTauLibPrepControl2: false,
        consumables: run.consumables ?? {},
      };
    }
    return {
      name: '',
      description: '',
      species: 'Human',
      noOfSamples: '12',
      assay_version: INSTRUMENT_RUN_ASSAY_VERSIONS[0],
      samples: [],
      useTauLibPrepControl2: false,
      consumables: {},
    };
  }, [mode, run, orgIdToName]);

  const schema = useMemo(
    () =>
      z.object({
        name: z
          .string()
          .min(1, t('create:run.validation.nameRequired'))
          .refine(v => !/['`]/.test(v), {
            message: t('create:run.validation.nameInvalidChars'),
          }),
        description: z.string().optional(),
        species: z.enum(ALLOWED_SPECIES),
        assay_version: z.enum(
          INSTRUMENT_RUN_ASSAY_VERSIONS as [InstrumentRunAssayVersion, ...InstrumentRunAssayVersion[]],
        ),
        noOfSamples: z.string().min(1),
        samples: z
          .array(
            z.object({
              name: z.string(),
              study_name: z.string(),
              org_name: z.string().optional(),
              well_position: z.string(),
              demux_index: z.number(),
              is_control: z.boolean().optional(),
              control_type: z.string().optional(),
              flow_cell: z.string().nullable().optional(),
              lane: z.number().nullable().optional(),
              library_group: z.string().nullable().optional(),
              sample_group: z.string().nullable().optional(),
            }),
          )
          .superRefine((rows, ctx) => {
            const byName = new Map<string, number[]>();
            rows.forEach((r, i) => {
              const k = r.name?.trim();
              if (!k) return;
              const arr = byName.get(k) ?? [];
              arr.push(i);
              byName.set(k, arr);
            });
            for (const idxs of byName.values()) {
              if (idxs.length > 1) {
                for (const i of idxs) {
                  ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: [i, 'name'],
                    message: t('create:run.validation.duplicateInRun'),
                  });
                }
              }
            }
          }),
        useTauLibPrepControl2: z.boolean().optional(),
        consumables: z.record(z.string(), z.string().nullable().optional()).optional(),
      }),
    [t],
  );

  // Backend per-row errors (from handleApiError) and client-side duplicate-name issues (from the
  // onChange Zod validator) are kept in SEPARATE state. They were previously one setter, so an
  // edit that passed Zod would clear ALL backend highlights (even untouched rows). Merged below
  // for SampleManager; backend highlights now persist until the user edits the affected row or resubmits.
  const [sampleServerErrors, setSampleServerErrors] = useState<Triple[]>([]);
  const [sampleClientErrors, setSampleClientErrors] = useState<Triple[]>([]);
  const sampleErrors = useMemo(
    () => [...sampleServerErrors, ...sampleClientErrors],
    [sampleServerErrors, sampleClientErrors],
  );

  // When the user edits a row, drop that row's backend error so it can't be revived by the next
  // serverErrors re-push (the merged array recomputes whenever client-side dupe errors change).
  const handleSampleRowEdit = useCallback((rowIndex: number) => {
    setSampleServerErrors(prev => prev.filter(triple => triple.row !== rowIndex));
  }, []);

  const handleSampleRowsBulkEdit = useCallback(() => {
    setSampleServerErrors([]);
  }, []);

  const form = useForm({
    defaultValues: initialValues,
    validators: {
      onChange: ({ value }) => {
        const result = schema.safeParse(value);
        if (result.success) {
          setSampleClientErrors([]);
          return undefined;
        }

        const dupes: Triple[] = [];
        for (const issue of result.error.issues) {
          const [arrField, rowIdx, field] = issue.path;
          if (arrField === 'samples' && typeof rowIdx === 'number' && field === 'name') {
            dupes.push({ row: rowIdx, field: 'name', msg: issue.message });
          }
        }
        setSampleClientErrors(dupes);

        return result.error.message;
      },
    },
    onSubmit: async ({ value }) => {
      await handleSubmit(value);
    },
  });

  const buildPayload = useCallback(
    (value: InstrumentRunFormValues) => ({
      assay_version: value.assay_version,
      name: value.name,
      description: value.description ?? null,
      species: value.species as InstrumentRunSpecies,
      num_samples: Number(value.noOfSamples) as InstrumentRunSampleSize,
      samples: value.samples.map(s => {
        const resolvedOrgId = s.org_id || (s.org_name ? (orgNameToId.get(s.org_name) ?? null) : null);
        return {
          name: s.name || null,
          study_name: s.study_name || null,
          org_id: resolvedOrgId,
          org_name: resolvedOrgId ? null : (s.org_name ?? null),
          well_position: s.well_position || null,
          demux_index: s.demux_index,
          is_control: s.is_control ?? null,
          control_type: s.control_type ?? null,
          flow_cell: s.flow_cell ?? null,
          lane: s.lane ?? null,
          library_group: s.library_group ?? null,
          sample_group: s.sample_group ?? null,
        };
      }),
    }),
    [orgNameToId],
  );

  const handleApiError = useCallback(
    (error: unknown, value: InstrumentRunFormValues) => {
      const fieldErrors = extractFieldErrors(error);
      const { formErrors, arrayErrors, unmapped } = mapFieldErrorsToForm(fieldErrors, {
        arrayFields: ['samples'],
        byNameLookup: { fieldName: 'name', rows: value.samples },
      });

      // Top-level field errors surface inline on the form. Mark the error as handled so callers
      // don't also fire the generic failure toast (the inline message is the user-facing signal).
      let handled = false;
      if (Object.keys(formErrors).length > 0) {
        applyServerFieldErrors(form, formErrors);
        handled = true;
      }

      const sampleErrs = arrayErrors.samples;
      if (sampleErrs?.length) {
        setSampleServerErrors(sampleErrs);
        // The kind:'row' backend envelope is a generic per-sample validation error, so only use
        // the name-conflict copy when the messages are actually conflicts; otherwise a neutral
        // "samples have errors" message. The precise reason is in each row's tooltip either way.
        const allNameConflicts = sampleErrs.every(e => /already exist/i.test(e.msg));
        toast.error(
          allNameConflicts
            ? t('create:run.error.sampleConflictsBackend', { count: sampleErrs.length })
            : t('create:run.error.sampleErrorsBackend', { count: sampleErrs.length }),
        );
        return true;
      }

      if (unmapped.length > 0) {
        toast.error(unmapped[0]);
        return true;
      }

      return handled;
    },
    [form, t],
  );

  const handleSaveDraft = useCallback(
    async (value: InstrumentRunFormValues) => {
      if (!value.name) {
        toast.error(t('create:run.validation.nameRequired'));
        return;
      }
      setSampleServerErrors([]);
      clearServerFieldErrors(form);
      try {
        const payload: InstrumentRunDraftCreate = { ...buildPayload(value) };
        const created = await api.post<InstrumentRunPublic>(instrumentRunQueryKeys.endpoints.draft(orgId), payload);
        if (!created) return;
        await applyInstrumentRunsListRefresh(orgId, queryClient, setShouldRefreshInstrumentRuns);
        toast.success(`${value.name} ${t('create:run.created')}`);
        void navigate({ to: '/instrument_runs/$instrumentRunId', params: { instrumentRunId: created.id } });
      } catch (error) {
        if (import.meta.env.DEV) console.error(error);
        setSampleServerErrors([]);
        const handled = handleApiError(error, value);
        if (!handled) toast.error(t('create:run.failedToSaveDraft'));
      }
    },
    [t, api, orgId, queryClient, setShouldRefreshInstrumentRuns, buildPayload, navigate, form, handleApiError],
  );

  const handleSubmit = useCallback(
    async (value: InstrumentRunFormValues) => {
      if (!value.name) {
        toast.error(t('create:run.validation.nameRequired'));
        return;
      }
      setSampleServerErrors([]);
      clearServerFieldErrors(form);
      try {
        const draftPayload: InstrumentRunDraftCreate = {
          ...buildPayload(value),
        };
        const draft = await api.post<InstrumentRunPublic>(instrumentRunQueryKeys.endpoints.draft(orgId), draftPayload);
        if (!draft) return;
        await api.post(instrumentRunQueryKeys.endpoints.submit(orgId, draft.id), {});
        await applyInstrumentRunsListRefresh(orgId, queryClient, setShouldRefreshInstrumentRuns);
        toast.success(`${value.name} ${t('create:run.created')}`);
        void navigate({
          to: '/instrument_runs/$instrumentRunId',
          params: { instrumentRunId: draft.id },
        });
        onSave();
      } catch (error) {
        if (import.meta.env.DEV) console.error(error);
        setSampleServerErrors([]);
        const handled = handleApiError(error, value);
        if (!handled) toast.error(t('create:run.failed'));
      }
    },
    [t, api, orgId, queryClient, setShouldRefreshInstrumentRuns, navigate, onSave, buildPayload, form, handleApiError],
  );

  const handleSaveEdit = useCallback(
    async (value: InstrumentRunFormValues) => {
      if (!run) return;
      if (!value.name) {
        toast.error(t('create:run.validation.nameRequired'));
        return;
      }
      setSampleServerErrors([]);
      clearServerFieldErrors(form);
      try {
        const payload: InstrumentRunDraftUpdate = { ...buildPayload(value) };
        const updated = await api.put<InstrumentRunPublic>(
          instrumentRunQueryKeys.endpoints.detail(orgId, run.id),
          payload,
        );
        if (!updated) return;
        await queryClient.invalidateQueries({
          queryKey: instrumentRunQueryKeys.detail(orgId, run.id),
        });
        await applyInstrumentRunsListRefresh(orgId, queryClient, setShouldRefreshInstrumentRuns);
        toast.success(`${value.name} ${t('create:run.updated')}`);
        onSave();
      } catch (error) {
        if (import.meta.env.DEV) console.error(error);
        setSampleServerErrors([]);
        const handled = handleApiError(error, value);
        if (!handled) toast.error(t('create:run.failed'));
      }
    },
    [run, t, api, orgId, queryClient, setShouldRefreshInstrumentRuns, onSave, buildPayload, form, handleApiError],
  );

  const handleSubmitDraft = useCallback(
    async (value: InstrumentRunFormValues) => {
      if (!run) return;
      if (!value.name) {
        toast.error(t('create:run.validation.nameRequired'));
        return;
      }
      // Submitting a draft finalizes it, so block in-run duplicate names here just like the
      // create-mode submit does (the duplicate cells are already highlighted by the onChange
      // Zod validator). handleSaveEdit, the plain save, stays unblocked on purpose.
      const dupes = countDuplicateSampleNames(value.samples);
      if (dupes > 0) {
        toast.error(t('create:run.error.sampleConflictsInRun', { count: dupes }));
        return;
      }
      setSampleServerErrors([]);
      clearServerFieldErrors(form);
      try {
        const payload: InstrumentRunDraftUpdate = { ...buildPayload(value) };
        const updated = await api.put<InstrumentRunPublic>(
          instrumentRunQueryKeys.endpoints.detail(orgId, run.id),
          payload,
        );
        if (!updated) return;
        await api.post(instrumentRunQueryKeys.endpoints.submit(orgId, run.id), {});
        await queryClient.invalidateQueries({
          queryKey: instrumentRunQueryKeys.detail(orgId, run.id),
        });
        await applyInstrumentRunsListRefresh(orgId, queryClient, setShouldRefreshInstrumentRuns);
        toast.success(`${value.name} ${t('create:run.submitted')}`);
        void navigate({ to: '/instrument_runs/$instrumentRunId', params: { instrumentRunId: run.id } });
      } catch (error) {
        if (import.meta.env.DEV) console.error(error);
        setSampleServerErrors([]);
        const handled = handleApiError(error, value);
        if (!handled) toast.error(t('create:run.failed'));
      }
    },
    [run, t, api, orgId, queryClient, setShouldRefreshInstrumentRuns, buildPayload, navigate, form, handleApiError],
  );

  // Create-mode submit: surface in-table duplicate sample names with an explicit toast
  // (distinct from the backend "already exists" conflict toast). form.handleSubmit() then
  // re-runs validation, which keeps the duplicate cells highlighted and blocks the API call.
  const submitCreate = useCallback(() => {
    const dupes = countDuplicateSampleNames(form.state.values.samples);
    if (dupes > 0) {
      toast.error(t('create:run.error.sampleConflictsInRun', { count: dupes }));
    }
    void form.handleSubmit();
  }, [form, t]);

  useImperativeHandle(
    ref,
    () => ({
      submit: () => {
        if (mode === 'edit') void handleSaveEdit(form.state.values);
        else submitCreate();
      },
      saveDraft: () => {
        if (mode === 'edit') void handleSaveEdit(form.state.values);
        else void handleSaveDraft(form.state.values);
      },
      submitDraft: () => void handleSubmitDraft(form.state.values),
      form: form as unknown as InstrumentRunForm,
    }),
    [mode, form, handleSaveEdit, handleSaveDraft, handleSubmitDraft, submitCreate],
  );

  const noOfSamplesStr = useStore(form.store, s => s.values.noOfSamples);
  const noOfSamples = Number(noOfSamplesStr ?? 12);
  const assayVersion = useStore(form.store, s => s.values.assay_version);
  const useTauLibPrepControl2 = useStore(form.store, s => s.values.useTauLibPrepControl2 ?? false);
  const flowcellSns = run?.consumables?.three_flow_cell_ids?.split(',').map(s => s.trim());
  const ownerOrgName = orgIdToName.get(String(orgId)) ?? '';

  return (
    <Stack className={styles.container} fullWidth gap={4} orientation="vertical">
      {showHeader && (
        <Stack className={irStyles.headerCard} fullWidth orientation="vertical">
          {/* Name + Assay Version */}
          <Grid
            alignItems="center"
            className={irStyles.headerTitleRow}
            fullWidth
            gap={6}
            gridTemplateColumns="1fr .2fr"
          >
            <form.Field name="name">
              {field => (
                <Input
                  data-testid="run-name-input"
                  fullWidth
                  id="run-name"
                  label={{
                    children: t('header.name', { ns: 'runs' }),
                    htmlFor: 'run-name',
                    id: 'label-run-name',
                  }}
                  onChange={e => field.handleChange((e.target as HTMLInputElement).value)}
                  value={(field.state.value as string) ?? ''}
                />
              )}
            </form.Field>
            <form.Field name="assay_version">
              {field => (
                <Select
                  allowClear={false}
                  classNames={{
                    button: irStyles.headerSelect,
                    container: irStyles.assaySelect,
                  }}
                  data-testid="select-assay-version"
                  id="assay-version"
                  label={{
                    children: t('header.assay_version', { ns: 'runs' }),
                    id: 'label-assay-version',
                  }}
                  onChange={idx => {
                    const val = INSTRUMENT_RUN_ASSAY_VERSIONS[idx];
                    if (val !== undefined) field.handleChange(val);
                  }}
                  options={[...INSTRUMENT_RUN_ASSAY_VERSIONS]}
                  value={INSTRUMENT_RUN_ASSAY_VERSIONS.indexOf(field.state.value as InstrumentRunAssayVersion)}
                />
              )}
            </form.Field>
          </Grid>

          {/* Description */}
          <form.Field name="description">
            {field => (
              <Textarea
                data-testid="run-description-textarea"
                label={{
                  children: t('header.description', { ns: 'runs' }),
                  htmlFor: 'run-description',
                  id: 'label-run-description',
                }}
                onChange={e => field.handleChange((e.target as HTMLTextAreaElement).value)}
                value={(field.state.value as string) ?? ''}
              />
            )}
          </form.Field>

          {/* Samples + Species — 2 items each at 1/3 width, start-aligned */}
          <div className={irStyles.headerSelectsRow}>
            <div className={irStyles.headerSelectItem}>
              <form.Field name="noOfSamples">
                {field => (
                  <Select
                    allowClear={false}
                    classNames={{ button: irStyles.headerSelect }}
                    data-testid="select-no-of-samples"
                    id="no-of-samples"
                    label={{
                      children: t('header.samples', { ns: 'runs' }),
                      id: 'label-no-of-samples',
                    }}
                    onChange={idx => {
                      const val = NO_OF_SAMPLES_OPTIONS[idx];
                      if (val !== undefined) field.handleChange(val);
                    }}
                    options={[...NO_OF_SAMPLES_OPTIONS]}
                    value={NO_OF_SAMPLES_OPTIONS.indexOf(
                      (field.state.value as (typeof NO_OF_SAMPLES_OPTIONS)[number]) ?? '4',
                    )}
                  />
                )}
              </form.Field>
            </div>
            <div className={irStyles.headerSelectItem}>
              <form.Field name="species">
                {field => (
                  <Select
                    allowClear={false}
                    classNames={{ button: irStyles.headerSelect }}
                    data-testid="select-species"
                    id="species"
                    label={{
                      children: t('header.species', { ns: 'runs' }),
                      id: 'label-species',
                    }}
                    onChange={idx => {
                      const val = ALLOWED_SPECIES[idx];
                      if (val !== undefined) field.handleChange(val);
                    }}
                    options={[...ALLOWED_SPECIES]}
                    value={ALLOWED_SPECIES.indexOf(field.state.value as (typeof ALLOWED_SPECIES)[number])}
                  />
                )}
              </form.Field>
            </div>
          </div>
        </Stack>
      )}

      {/* Controls — display only, computed from assay version + sample count */}
      <ControlsSection
        assayVersion={assayVersion}
        flowcellSns={flowcellSns}
        numSamples={noOfSamples}
        ownerOrgName={ownerOrgName}
        useTauLibPrepControl2={useTauLibPrepControl2}
      />

      {assayVersion === 'Tau' && noOfSamples === 12 && (
        <form.Field name="useTauLibPrepControl2">
          {field => (
            <CheckboxGroup
              data-testid="use-a4-control-checkbox"
              id="use-a4-control"
              onChange={values => field.handleChange(values.includes('a4'))}
              options={[{ value: 'a4', name: 'a4', children: t('controls.useA4Control', { ns: 'runs' }) }]}
              value={(field.state.value as boolean) ? ['a4'] : []}
            />
          )}
        </form.Field>
      )}

      {/* Samples — non-control rows only */}
      <SampleManager
        form={form as unknown as InstrumentRunForm}
        noOfSamples={noOfSamples}
        onSampleRowEdit={handleSampleRowEdit}
        onSampleRowsBulkEdit={handleSampleRowsBulkEdit}
        readOnlyColumns={mode === 'edit' ? EDIT_READONLY_COLUMNS : []}
        serverErrors={sampleErrors}
        showCopyToRows={mode === 'edit'}
      />

      {/* Consumables — read-only; backend draft schema does not accept consumables */}
      <InstrumentRunConsumables
        isEditing={false}
        values={form.state.values.consumables as Partial<InstrumentRunConsumablesType>}
      />

      {/* Footer — only shown when buttons are not hosted in a parent action row */}
      {!showHeader && (
        <Stack className={styles.footer} fullWidth gap={3} justifyContent="end" orientation="horizontal">
          {onCancel && (
            <Button data-testid="run-edit-cancel" onPress={onCancel} visual="outline">
              <XIcon size={BUTTON_ICON_SIZE} />
              {t('cancel', { ns: 'general' })}
            </Button>
          )}

          {mode === 'create' && (
            <Button
              data-testid="run-save-draft"
              onPress={() => void handleSaveDraft(form.state.values)}
              visual="outline"
            >
              <SaveIcon size={BUTTON_ICON_SIZE} />
              {t('create:run.saveAsDraft')}
            </Button>
          )}

          {mode === 'edit' && run?.status === 'DRAFT' && (
            <Button
              data-testid="run-save-draft"
              onPress={() => void handleSaveEdit(form.state.values)}
              visual="outline"
            >
              <SaveIcon size={BUTTON_ICON_SIZE} />
              {t('create:run.saveAsDraft')}
            </Button>
          )}

          <Button
            data-testid={mode === 'edit' ? 'run-save' : 'run-submit'}
            onPress={() => {
              if (mode === 'edit') {
                void handleSaveEdit(form.state.values);
              } else {
                submitCreate();
              }
            }}
            visual="primary"
          >
            <CloudUploadIcon size={BUTTON_ICON_SIZE} />
            {mode === 'edit' ? t('save', { ns: 'general' }) : t('create:run.submit', { defaultValue: 'Submit' })}
          </Button>
        </Stack>
      )}
    </Stack>
  );
});

RunEditForm.displayName = 'RunEditForm';

export default RunEditForm;
